import { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader, type IScannerControls } from '@zxing/browser';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { createRetailBarcodeHints, normalizeBarcodeValue, playBarcodeScanBeep } from '@/lib/barcodeScan';
import { cn } from '@/lib/utils';
import { Camera, RefreshCw, SwitchCamera } from 'lucide-react';

export interface BarcodeScannerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onScan: (value: string) => void;
  title?: string;
  description?: string;
}

type ScannerPhase = 'idle' | 'starting' | 'scanning' | 'error';

export function BarcodeScannerDialog({
  open,
  onOpenChange,
  onScan,
  title = 'Scan barcode',
  description = 'Point your camera at the barcode. Scanning works best with good lighting and a steady view.',
}: BarcodeScannerDialogProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);

  const [phase, setPhase] = useState<ScannerPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceIndex, setDeviceIndex] = useState(0);

  const stopScanner = useCallback(() => {
    controlsRef.current?.stop();
    controlsRef.current = null;
    const video = videoRef.current;
    if (video?.srcObject instanceof MediaStream) {
      video.srcObject.getTracks().forEach((track) => track.stop());
      video.srcObject = null;
    }
  }, []);

  const startScanner = useCallback(async () => {
    stopScanner();
    setError(null);
    setPhase('starting');

    try {
      if (!readerRef.current) {
        readerRef.current = new BrowserMultiFormatReader(createRetailBarcodeHints());
      }

      let available = devices;
      if (available.length === 0) {
        available = await BrowserMultiFormatReader.listVideoInputDevices();
        setDevices(available);
      }

      if (available.length === 0) {
        throw new Error('No camera found on this device.');
      }

      const deviceId = available[deviceIndex]?.deviceId;
      const video = videoRef.current;
      if (!video) {
        throw new Error('Camera preview is not ready.');
      }

      const controls = await readerRef.current.decodeFromVideoDevice(
        deviceId,
        video,
        (result, err) => {
          if (result) {
            const value = normalizeBarcodeValue(result.getText());
            if (value) {
              playBarcodeScanBeep();
              stopScanner();
              setPhase('idle');
              onScan(value);
              onOpenChange(false);
            }
          }
          if (err && err.name !== 'NotFoundException') {
            console.debug('Barcode scan frame error:', err.message);
          }
        }
      );

      controlsRef.current = controls;
      setPhase('scanning');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not start the camera.';
      setError(message);
      setPhase('error');
      stopScanner();
    }
  }, [deviceIndex, devices, onOpenChange, onScan, stopScanner]);

  const startScannerRef = useRef(startScanner);
  startScannerRef.current = startScanner;

  useEffect(() => {
    if (!open) {
      stopScanner();
      setPhase('idle');
      setError(null);
      return undefined;
    }

    void startScannerRef.current();
    return () => {
      stopScanner();
    };
  }, [open, deviceIndex, stopScanner]);

  function flipCamera() {
    if (devices.length < 2) return;
    setDeviceIndex((prev) => (prev + 1) % devices.length);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md gap-4">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div
          className={cn(
            'relative overflow-hidden rounded-lg border bg-black aspect-[4/3]',
            phase === 'scanning' && 'ring-2 ring-primary/40'
          )}
        >
          <video
            ref={videoRef}
            className="h-full w-full object-cover"
            muted
            playsInline
            autoPlay
          />
          {phase !== 'scanning' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70 px-6 text-center text-sm text-white">
              <Camera className="h-8 w-8 opacity-80" />
              {phase === 'starting' && <p>Starting camera…</p>}
              {phase === 'idle' && !error && <p>Camera stopped</p>}
              {error && <p className="text-red-200">{error}</p>}
            </div>
          )}
          {phase === 'scanning' && (
            <div className="pointer-events-none absolute inset-x-8 top-1/2 h-28 -translate-y-1/2 rounded-md border-2 border-white/70" />
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="default"
            onClick={() => void startScanner()}
            disabled={phase === 'starting'}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            {phase === 'scanning' ? 'Restart camera' : 'Start camera'}
          </Button>
          {devices.length > 1 && (
            <Button type="button" variant="outline" onClick={flipCamera}>
              <SwitchCamera className="mr-2 h-4 w-4" />
              Flip camera
            </Button>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          Supports UPC, EAN-13, EAN-8, Code 128, ITF-14, and other common retail formats.
        </p>
      </DialogContent>
    </Dialog>
  );
}
